import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { ScheduledRedirectNotice } from '../jsxElements/ScheduledRedirectNotice';
import {
    GlobalInformationAlertValue,
    GlobalInformationAlertType,
} from '../types';

export type InformationalAlertContextValue = {
    informationalAlertContent: React.ReactNode | null;
    setInformationalAlertContent: (content: React.ReactNode | null) => void;
    clearInformationalAlertContent: () => void;
    clearScheduledRedirectNotice: () => void;
    showGlobalInformationAlert: (
        type: GlobalInformationAlertType,
        value: GlobalInformationAlertValue
    ) => void;
};

const InformationalAlertContext = createContext<
    InformationalAlertContextValue | undefined
>(undefined);

export const GlobalAlertProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [informationalAlertContent, setInformationalAlertContentState] =
        useState<React.ReactNode | null>(null);
    const [activeAlertType, setActiveAlertType] =
        useState<GlobalInformationAlertType | null>(null);

    const setInformationalAlertContent = useCallback(
        (content: React.ReactNode | null) => {
            setActiveAlertType(null);
            setInformationalAlertContentState(content);
        },
        []
    );

    const clearInformationalAlertContent = useCallback(() => {
        setActiveAlertType(null);
        setInformationalAlertContentState(null);
    }, []);

    const clearScheduledRedirectNotice = useCallback(() => {
        if (
            activeAlertType !==
            GlobalInformationAlertType.ScheduledRedirectNotice
        ) {
            return;
        }

        setActiveAlertType(null);
        setInformationalAlertContentState(null);
    }, [activeAlertType]);

    const showGlobalInformationAlert = useCallback(
        (
            type: GlobalInformationAlertType,
            value: GlobalInformationAlertValue
        ) => {
            switch (type) {
                case GlobalInformationAlertType.ScheduledRedirectNotice: {
                    const alertKey = [
                        type,
                        value.target,
                        value.delayMs ?? '',
                        value.amplitudeEvent?.eventType ?? '',
                    ].join(':');

                    setActiveAlertType(type);
                    setInformationalAlertContentState(
                        <ScheduledRedirectNotice
                            key={alertKey}
                            callback={() => {
                                // Clear the alert content when the redirect is triggered to prevent stale content if the same alert needs to be shown again in the future
                                clearInformationalAlertContent();
                                if (value.redirectCallback) {
                                    value.redirectCallback();
                                }
                            }}
                            {...value}
                        />
                    );
                    break;
                }
            }
        },
        []
    );

    const value = useMemo(
        () => ({
            informationalAlertContent,
            setInformationalAlertContent,
            clearInformationalAlertContent,
            clearScheduledRedirectNotice,
            showGlobalInformationAlert,
        }),
        [
            informationalAlertContent,
            clearInformationalAlertContent,
            clearScheduledRedirectNotice,
            setInformationalAlertContent,
            showGlobalInformationAlert,
        ]
    );

    return (
        <InformationalAlertContext.Provider value={value}>
            {children}
        </InformationalAlertContext.Provider>
    );
};

export const useInformationalAlert = (): InformationalAlertContextValue => {
    const context = useContext(InformationalAlertContext);

    if (!context) {
        throw new Error(
            'useInformationalAlert must be used within a GlobalAlertProvider'
        );
    }

    return context;
};

export const useOptionalInformationalAlert = () =>
    useContext(InformationalAlertContext);
